import React, { useState, useMemo, useCallback } from 'react';
import { Button, Icon } from 'semantic-ui-react';
import Table from 'components/Table';
import Detalhamento from './Detalhamento';
import { numFormat } from '@rededompedro/utils';
import { toast } from 'react-toastify';
import { tap, finalize } from 'rxjs/operators';
import request from 'services/http';
import produce from 'immer';
import { useSubscription } from 'utils';
import XLSX from 'xlsx';

export default function RelatoriosExibicao({
    registros,
    setRegistros,
    form,
    operacoes,
    tipos,
    periodos,
    empresas,
}) {
    const [detalhamento, setDetalhamento] = useState(null);
    const [consolidado, setConsolidado] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [emp, setEmp] = useState(null);
    const [empToConsolidate, setEmpToConsolidate] = useState(null);

    function clearState() {
        if (detalhamento && detalhamento.length > 0) {
            setDetalhamento(null);
            setEmp(null);
        } else setRegistros([]);
    }

    function detalhar(empresa, consolidado) {
        empresa = empresas.find((x) => x && x._id === empresa)
            ? empresas.find((x) => x && x._id === empresa)
            : empresas.find((x) => x && x.codDpnet === empresa);
        setEmp(empresa);
        setConsolidado(consolidado);
        setIsSubmitting(true);
    }

    function consolidar(empresa) {
        empresa = empresas.find((x) => x && x._id === empresa)
            ? empresas.find((x) => x && x._id === empresa)
            : empresas.find((x) => x && x.codDpnet === empresa);
        setEmpToConsolidate(empresa);
        setLoading(true);
    }

    const detalharPdd$ = useMemo(
        () =>
            request({
                url: `/dre/pdd/relatorios/${emp ? emp._id : ''}`,
                method: 'POST',
                body: form,
            }).pipe(
                tap(({ response }) => {
                    if ('faturas' in response || 'cheques' in response) {
                        if (
                            (response.faturas && !response.faturas.length) ||
                            (response.cheques && !response.cheques.length)
                        )
                            toast.info('Nenhum registro encontrado.');
                        setDetalhamento(
                            'faturas' in response ? response.faturas : response.cheques,
                        );
                    }
                }),
                finalize(() => setIsSubmitting(false)),
            ),
        [form, emp, setDetalhamento],
    );

    const consolidarPdd$ = useMemo(
        () =>
            request({
                url: '/dre/pdd/consolidar',
                method: 'POST',
                body: { ...form, empresa: empToConsolidate ? empToConsolidate._id : '' },
            }).pipe(
                tap(() => {
                    toast.success(
                        `${form.operacao === 1 ? 'Aproveitamento' : 'Dedução'} no periodo ${
                            periodos.find((x) => x && x.id === form.periodo).descricao
                        } para a empresa ${
                            empToConsolidate ? empToConsolidate.nomeEmpresa : ''
                        } consolidado!`,
                    );
                    const reg = produce(registros, (draft) => {
                        const idx = draft.findIndex(
                            (x) =>
                                x &&
                                ((form.tipo !== 0 && x.empresa === empToConsolidate._id) ||
                                    (form.tipo === 0 && x.empresa === empToConsolidate.codDpnet)),
                        );
                        draft[idx].consolidado = true;
                    });
                    setRegistros(reg);
                    setEmpToConsolidate(null);
                }),
                finalize(() => setLoading(false)),
            ),
        [form, empToConsolidate, registros, setRegistros, setEmpToConsolidate, periodos],
    );

    const exportData = useCallback(() => {
        if (registros.length) {
            const regs = registros.map((it) => ({
                Empresa: empresas.find(
                    (y) =>
                        y &&
                        ((form.tipo === 0 && y.codDpnet === it.empresa) ||
                            (form.tipo !== 0 && y._id === it.empresa)),
                ).nomeEmpresa,
                Total: it.valor.toFixed(2),
                Consolidado: it.consolidado ? 'Sim' : 'Não',
            }));
            const file = XLSX.utils.book_new();
            const sheet = XLSX.utils.json_to_sheet(regs);
            XLSX.utils.book_append_sheet(
                file,
                sheet,
                periodos.find((x) => x && x.id === form.periodo).descricao,
            );
            const tipo = tipos
                .find((x) => x && x.tipo_entrada === form.tipo)
                .descricao.toLowerCase()
                .trim()
                .replace(/-/g, '')
                .replace(/\s+/g, '_');
            XLSX.writeFile(file, `pdd_${tipo}.xlsx`);
        }
    }, [empresas, form.periodo, form.tipo, periodos, registros, tipos]);

    useSubscription(detalharPdd$, isSubmitting);
    useSubscription(consolidarPdd$, loading && empToConsolidate !== null);

    const config = {
        columns: [
            { field: 'empresa', text: 'Empresa', sort: true },
            { field: 'total', text: 'Total', formatter: (value) => numFormat(value), sort: true },
            { field: 'consolidado', text: 'Situação', sort: false },
        ],
        footer: [
            {
                columns: [
                    { colSpan: 1, content: 'Total', textAlign: 'center' },
                    {
                        colSpan: 2,
                        content: numFormat(
                            registros.map((x) => x.valor).reduce((a, b) => a + b, 0),
                        ),
                    },
                ],
            },
        ],
        data: registros.length
            ? registros.map((x) => {
                  console.log(x);
                  return {
                      empresa: (
                          <span
                              className="anchor"
                              onClick={() => detalhar(x.empresa, x.consolidado)}
                          >
                              {
                                  empresas.find(
                                      (y) =>
                                          y &&
                                          ((form.tipo === 0 && y.codDpnet === x.empresa) ||
                                              (form.tipo !== 0 && y._id === x.empresa)),
                                  ).nomeEmpresa
                              }
                          </span>
                      ),
                      total: x.valor,
                      consolidado: x.consolidado ? (
                          <Icon
                              name="check"
                              color="green"
                              children={
                                  <span
                                      style={{ fontSize: 16, fontWeight: 'bold' }}
                                      className="px-2"
                                  >
                                      Consolidado
                                  </span>
                              }
                          />
                      ) : (
                          <Button
                              size="mini"
                              className="btn-primary"
                              loading={loading && x.empresa === empToConsolidate._id}
                              disabled={loading}
                              onClick={() => consolidar(x.empresa)}
                          >
                              Consolidar
                          </Button>
                      ),
                  };
              })
            : [],
    };

    const detalhamentoProps = {
        setDetalhamento,
        detalhamento,
        tipo: form.tipo,
        operacao: form.operacao,
        periodo: form.periodo,
        registros,
        setRegistros,
        emp,
        setEmp,
        consolidado,
    };

    return (
        <>
            <div className="row">
                <div className="col-12 col-md d-flex align-items-end">
                    <p className="mb-0">
                        <strong>Empresa: </strong>{' '}
                        {emp !== null
                            ? empresas.find((x) => x && x._id === emp._id).nomeEmpresa
                            : form.empresa === 999
                            ? 'Todas'
                            : empresas.find((x) => x && x._id === form.empresa).nomeEmpresa}{' '}
                        - {periodos.find((x) => x && x.id === form.periodo).descricao}
                        <span className="px-1">/</span>
                        <strong>Operação: </strong>
                        {operacoes.find((x) => x && x.id === form.operacao).descricao} -{' '}
                        {tipos.find((x) => x && x.tipo_entrada === form.tipo).descricao}
                    </p>
                </div>
                <div className="col-12 col-md-4 d-print-none">
                    <Button
                        size="tiny"
                        className="btn-red"
                        floated="right"
                        onClick={clearState}
                        content="Voltar"
                        icon="arrow left"
                    />
                    <Button
                        size="tiny"
                        className="ml-auto btn-primary"
                        floated="right"
                        onClick={() => window.print()}
                        content="Imprimir"
                        icon="print"
                    />
                    <Button
                        size="tiny"
                        color="teal"
                        className="mr-1"
                        floated="right"
                        icon="file excel outline"
                        content="Exportar"
                        onClick={exportData}
                    />
                </div>
            </div>
            {!detalhamento && (
                <Table
                    id="table-relatorios-exibicao"
                    size="small"
                    compact
                    fixed
                    sortable
                    selectable
                    {...config}
                />
            )}
            {detalhamento && detalhamento.length > 0 && <Detalhamento {...detalhamentoProps} />}
        </>
    );
}
